import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  DemurrageDetentionListDto,
  DemurrageDetentionShipmentModel,
  DemurrageDetentionStatisticFiltersModel,
} from '../models/demurrage-detention/demurrage-detention-list.model';
import { DemurrageDetentionListService } from './demurrage-detention-list.service';
import {
  DemurrageDetentionExportToFileStart,
  DemurrageDetentionInitialState,
  DemurrageDetentionLoad,
  DemurrageDetentionStatisticsLoad,
} from './demurrage-detention-list.actions';
import { ShipmentQuickFilterModel } from '../../models';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { NotificationService } from '../../../../../common';
import { Observable, of, Subject } from 'rxjs';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { CONTAINER_CHARGES } from '../constants/demurrage-detention-statistic-filters.constant';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import {
  DemurrageDetentionStatisticFilterKeysEnum,
  DemurrageDetentionStatisticFilterModel,
} from '../models/shipment-filters.model';

export interface DemurrageDetentionListStateModel {
  statistic: DemurrageDetentionStatisticFiltersModel;
  listDto: DemurrageDetentionListDto;
  loaded: boolean;
  statisticLoaded: boolean;
}

const initialState: DemurrageDetentionListStateModel = {
  statistic: {
    containerCharges: CONTAINER_CHARGES,
  },
  listDto: null,
  loaded: false,
  statisticLoaded: false,
};

@State<DemurrageDetentionListStateModel>({
  name: 'demurrageDetention',
  defaults: initialState,
})
@Injectable()
export class DemurrageDetentionListState {
  private stopStatisticsRequest$: Subject<void> = new Subject();

  constructor(
    private demurrageAndDetentionService: DemurrageDetentionListService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getListDto(state: DemurrageDetentionListStateModel): DemurrageDetentionListDto {
    return state.listDto;
  }

  @Selector()
  static getItems(state: DemurrageDetentionListStateModel): DemurrageDetentionShipmentModel[] {
    return state.listDto?.items ?? [];
  }

  @Selector()
  static getContainerChargesStatistics(state: DemurrageDetentionListStateModel): ShipmentQuickFilterModel[] {
    return state.statistic.containerCharges;
  }

  @Selector()
  static getLoaded(state: DemurrageDetentionListStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getStatisticLoaded(state: DemurrageDetentionListStateModel): boolean {
    return state.statisticLoaded;
  }

  @Selector()
  static getTotalElements(state: DemurrageDetentionListStateModel): number {
    return state.listDto?.totalElements ?? 0;
  }

  @Action(DemurrageDetentionLoad)
  getDemurrageDetentionList(
    ctx: StateContext<DemurrageDetentionListStateModel>,
    { payload: { searchCriteria } }: DemurrageDetentionLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.demurrageAndDetentionService.getList(searchCriteria).pipe(
      map((listDto: DemurrageDetentionListDto) => {
        ctx.patchState({ listDto, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DemurrageDetentionStatisticsLoad)
  getDemurrageDetentionStatistics(
    ctx: StateContext<DemurrageDetentionListStateModel>,
    { payload: { searchCriteria } }: DemurrageDetentionLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    const activeStatistic = this.getActiveStatisticFilters(searchCriteria.dndStatisticFilter, ctx.getState().statistic);

    ctx.patchState({ statistic: activeStatistic, statisticLoaded: false });

    this.stopStatisticsRequest$.next();

    return this.demurrageAndDetentionService.getStatistics(searchCriteria).pipe(
      takeUntil(this.stopStatisticsRequest$),
      map((statistic: DemurrageDetentionStatisticFiltersModel) => {
        ctx.patchState({ statistic, statisticLoaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        ctx.patchState({ statisticLoaded: true });
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(DemurrageDetentionExportToFileStart)
  startExportToFile(
    ctx: StateContext<unknown>,
    { payload: { dialogRef, navigationNeeded, ...body } }: DemurrageDetentionExportToFileStart,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.demurrageAndDetentionService.startExportToFile(body).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.export.success.exportStart');
        dialogRef.close();
        if (navigationNeeded) {
          this.navigateUserService.navigateUserToAbsolutePath('/dashboard/exports');
        }
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DemurrageDetentionInitialState)
  setInitialState(ctx: StateContext<DemurrageDetentionListStateModel>): void {
    this.stopStatisticsRequest$.next();

    ctx.setState(initialState);
  }

  private getActiveStatisticFilters(
    filters: DemurrageDetentionStatisticFilterModel,
    statistic: DemurrageDetentionStatisticFiltersModel,
  ): DemurrageDetentionStatisticFiltersModel {
    const KEYS_MAP: Record<DemurrageDetentionStatisticFilterKeysEnum, keyof DemurrageDetentionStatisticFiltersModel> = {
      [DemurrageDetentionStatisticFilterKeysEnum.ContainerCharges]: 'containerCharges',
    };

    if (!filters || !statistic) {
      return statistic;
    }

    return Object.keys(filters).reduce(
      (acc: DemurrageDetentionStatisticFiltersModel, key: DemurrageDetentionStatisticFilterKeysEnum) => {
        if (filters[key]) {
          acc = {
            ...acc,
            [KEYS_MAP[key]]: acc[KEYS_MAP[key]].map((item: ShipmentQuickFilterModel) => ({
              ...item,
              active: item.name === filters[key],
            })),
          };
        }
        return acc;
      },
      { ...statistic },
    );
  }
}
